import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CryptoIcon } from "src/Common";
import styled from "@emotion/styled";
import { Top10Prices } from "../types";

interface Top10Cache {
  top10Cryptos: Top10Prices["top10Cryptos"];
  filters: any;
}

interface Props {
  top10Cryptos: Top10Prices["top10Cryptos"];
  top10CryptosBySymbol: Top10Prices["top10CryptosBySymbol"];
  filters: any;
  enable?: boolean;
}

export const useTop10ArbitrageAlert = ({
  top10Cryptos,
  top10CryptosBySymbol,
  filters,
}: Props) => {
  const [cache, setCache] = useState<Top10Cache | null>(null);

  useEffect(() => {
    const refreshCache = (
      top10Cryptos: Top10Prices["top10Cryptos"],
      filters: any
    ) => {
      return top10Cryptos.length
        ? setCache({ top10Cryptos, filters })
        : setCache(null);
    };

    function symmetricDifference(a1: string[], a2: string[]): string[] {
      return a1
        .filter((x) => !a2.includes(x))
        .concat(a2.filter((x) => !a1.includes(x)));
    }

    const compareTop10 = (cacheTop10: string[], responseTop10: string[]) => {
      const diff = symmetricDifference(cacheTop10, responseTop10);

      if (diff.length) {
        refreshCache(responseTop10, filters);
        toast(
          <ToastComponent
            diff={diff}
            newTop10={responseTop10}
            top10CryptosBySymbol={top10CryptosBySymbol}
          />,
          {
            theme: "dark",
            autoClose: 8000,
            pauseOnFocusLoss: false,
          }
        );
      }
    };

    if (cache && isEqual(cache?.filters, filters)) {
      !!top10Cryptos.length && compareTop10(cache.top10Cryptos, top10Cryptos);
    } else {
      refreshCache(top10Cryptos, filters);
    }
  }, [top10Cryptos, top10CryptosBySymbol, filters, cache]);

  return {};
};

const ToastComponent = ({
  diff,
  newTop10,
  top10CryptosBySymbol,
}: {
  diff: string[];
  newTop10: string[];
  top10CryptosBySymbol: Top10Prices["top10CryptosBySymbol"];
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Title>Top 10 update</Title>
      {diff.map((symbol: string, index: number) => {
        const cryptoIndex = newTop10.indexOf(symbol);
        return (
          <ContainerFlex key={symbol + index} style={{ margin: "0.5rem 0" }}>
            <CryptoIcon
              slug={top10CryptosBySymbol[symbol]?.slug ?? ""}
              style={{ height: "1.5rem" }}
            />
            <ContainerFlex>
              <Symbol>{symbol}</Symbol>
              <SymbolIndex isInTop10={cryptoIndex < 10 && cryptoIndex !== -1}>
                {cryptoIndex !== -1 ? cryptoIndex + 1 : "out"}
              </SymbolIndex>
            </ContainerFlex>
          </ContainerFlex>
        );
      })}
    </div>
  );
};

const ContainerFlex = styled.div({
  display: "flex",
  alignItems: "center",
});

const Title = styled.h3({
  fontWeight: 600,
});

const Symbol = styled.div({
  padding: "0 0.5rem",
  fontWeight: 600,
  fontSize: "0.9rem",
});

const SymbolIndex = styled.div(({ isInTop10 }: { isInTop10: boolean }) => ({
  color: isInTop10 ? "#25DD1E" : "#DD1E1E",
  fontWeight: 600,
  fontSize: "0.7rem",
}));
